import { VatControlLineDto } from "./ControlTVA.dto";
import { AccountableAccount } from "../../types/AccountableAccountTable";

/**
 * Groups general ledger lines by VAT rate and calculates the total credit for each rate
 *
 * @param {VatControlLineDto[]} lines - Array of general ledger lines to be grouped
 * @param {AccountableAccount[]} accounts - Array of accountable accounts containing VAT rate information
 * @returns {Record<number, { vatRate: number; balance: number }>} An object where:
 *   - key: the VAT rate (number)
 *   - value: an object containing:
 *     - vatRate: the VAT rate (number)
 *     - balance: the sum of credits for all lines with this VAT rate (number) minus the sum of debits.
 *
 * @example
 * const lines = [
 *   { name: "Account1", credit: 100 },
 *   { name: "Account2", credit: 200 }
 * ];
 * const accounts = [
 *   { name: "Account1", vatRate: 20 },
 *   { name: "Account2", vatRate: 20 }
 * ];
 * const result = groupByVatRate(lines, accounts);
 * // Returns: { 20: { vatRate: 20, totalCredit: 300 } }
 */
type tvaAccount = { name: string; balance: number };
export function groupByVatRate(lines: VatControlLineDto[], accounts: AccountableAccount[]) {
    const groupedByVatRate: Record<
        number,
        { accounts: tvaAccount[]; vatRate: number; balance: number }
    > = {};

    lines.forEach((line) => {
        // Find the matching account
        const matchingAccount = accounts.find((account) => account.name === line.name);

        if (matchingAccount && matchingAccount.vatRate !== undefined) {
            const vatRate = matchingAccount.vatRate;

            // Initialize the group if it doesn't exist
            if (!groupedByVatRate[vatRate]) {
                groupedByVatRate[vatRate] = { accounts: [], vatRate, balance: 0 };
            }

            // Add the line's credit to the total for this vatRate
            groupedByVatRate[vatRate].balance -= line.balance;
            groupedByVatRate[vatRate].accounts.push({ name: line.name, balance: -line.balance });
        }
    });

    return groupedByVatRate;
}
